import { axiosInstance } from './apiCommon';

/**
 * Company switch keys (mirror EPDServices.Application/FeatureFlags/CompanyFeatureFlagKeys.cs).
 * The backend compares them ordinally, so they must be sent exactly as written here.
 */
export const CompanyFeatureFlagKey = {
  DynamicForms: 'company.dynamic-forms',
  EpdReadOnly: 'company.epd-read-only',
} as const;

export type CompanyFeatureFlagKeyValue = (typeof CompanyFeatureFlagKey)[keyof typeof CompanyFeatureFlagKey];

/**
 * One company switch with its last change; modifiedBy / modifiedAt are null when the switch has no row
 * (mirrors CompanyFeatureFlagStateModel in EPDServices.Application/Features/CompanyFeatureFlags/CompanyFeatureFlagsModel.cs).
 */
export interface CompanyFeatureFlagStateModel {
  isEnabled: boolean;
  modifiedBy?: string | null;
  modifiedAt?: string | null;
}

/** The company switches as a System Administrator sees them (mirrors CompanyFeatureFlagsAdminModel). */
export interface CompanyFeatureFlagsAdminModel {
  dynamicForms: CompanyFeatureFlagStateModel;
  readOnly: CompanyFeatureFlagStateModel;
}

/** Access status of one EPD copy (mirrors EpdAccessState in EPDServices.Domain/Model/Config). */
export type EpdAccessState = 'Editable' | 'ReadOnly' | 'Hidden';

/** Format of one EPD copy (mirrors EpdFormat in EPDServices.Domain/Model/Config). */
export type EpdFormat = 'SimpleWizard' | 'Compiler' | 'DynamicForms';

/** The copy a switch makes visible: the EPDHead copy (SimpleWizard) or the Dynamic Forms copy. */
export type EpdCopyVisibleFormat = 'SimpleWizard' | 'DynamicForms';

/**
 * One EPD copy of a company, Hidden copies included; status data only
 * (mirrors CompanyEpdAccessStatusModel in EPDServices.Application/Features/EpdAccess/Models/EpdAccessStatusModels.cs).
 * `headId` is the id shared by both copies of a migrated EPD; `status` is the copy's own status (no row = Editable),
 * `effectiveStatus` also applies the company lock and the company Dynamic Forms switch.
 */
export interface CompanyEpdAccessStatusModel {
  headId: string;
  epdHeadId?: string | null;
  documentHeadId?: string | null;
  registrationNumber?: number | null;
  name?: string | null;
  format: EpdFormat;
  status: EpdAccessState;
  effectiveStatus: EpdAccessState;
  reason?: string | null;
  hasOtherCopy: boolean;
  modifiedBy?: string | null;
  modifiedAt?: string | null;
}

/** PUT status body: exactly one of epdHeadId (SimpleWizard, Compiler) and documentHeadId (Dynamic Forms). */
export interface SetEpdAccessStatusModel {
  epdHeadId?: string | null;
  documentHeadId?: string | null;
  status: EpdAccessState;
  reason?: string | null;
}

/** The answer of both switch endpoints: how many migrated EPDs were switched. */
export interface EpdCopySwitchResultModel {
  switched: number;
}

/** System Administrator only. Every EPD copy of the company, Hidden ones included. 404 for an unknown company. */
export const getAdminCompanyEpdAccessStatuses = async (companyId: string): Promise<CompanyEpdAccessStatusModel[]> =>
  (
    await axiosInstance.get<CompanyEpdAccessStatusModel[]>(
      `/api/admin/v1/epd-access/companies/${encodeURIComponent(companyId)}/epds`
    )
  ).data;

/**
 * System Administrator only. Sets the access status of one EPD copy and returns the stored row (without name, registration
 * number or hasOtherCopy - refetch the list). 400 (message in `detail`) for both or neither id, or for making a copy
 * visible while the other copy of the same EPD is visible (use the switch); 404 for an unknown EPD.
 */
export const setAdminEpdAccessStatus = async (model: SetEpdAccessStatusModel): Promise<void> => {
  await axiosInstance.put('/api/admin/v1/epd-access/status', model);
};

/** System Administrator only. Makes one copy of a migrated EPD visible and hides the other. 400 when there is no DF copy. */
export const switchAdminEpdCopy = async (
  headId: string,
  visibleFormat: EpdCopyVisibleFormat
): Promise<EpdCopySwitchResultModel> =>
  (await axiosInstance.post<EpdCopySwitchResultModel>('/api/admin/v1/epd-access/switch', { headId, visibleFormat })).data;

/** System Administrator only. Switches every migrated EPD of the company. 404 for an unknown company. */
export const switchAdminCompanyEpdCopies = async (
  companyId: string,
  visibleFormat: EpdCopyVisibleFormat
): Promise<EpdCopySwitchResultModel> =>
  (
    await axiosInstance.post<EpdCopySwitchResultModel>(
      `/api/admin/v1/epd-access/companies/${encodeURIComponent(companyId)}/switch`,
      { visibleFormat }
    )
  ).data;

/** System Administrator only. 404 for an unknown company. */
export const getAdminCompanyFeatureFlags = async (companyId: string): Promise<CompanyFeatureFlagsAdminModel> =>
  (
    await axiosInstance.get<CompanyFeatureFlagsAdminModel>(
      `/api/admin/v1/AdminCompanies/${encodeURIComponent(companyId)}/FeatureFlags`
    )
  ).data;

/**
 * System Administrator only. Sets one company switch and returns both switches as stored.
 * 400 (message in `detail`) for an unknown key or for turning Dynamic Forms off while a migrated EPD uses
 * its Dynamic Forms copy; 404 for an unknown company.
 */
export const setAdminCompanyFeatureFlag = async (
  companyId: string,
  key: CompanyFeatureFlagKeyValue,
  isEnabled: boolean
): Promise<CompanyFeatureFlagsAdminModel> =>
  (
    await axiosInstance.put<CompanyFeatureFlagsAdminModel>(
      `/api/admin/v1/AdminCompanies/${encodeURIComponent(companyId)}/FeatureFlags`,
      { key, isEnabled }
    )
  ).data;
